/**
 * Tax Submission Report Generator for CA Filing
 * Indian GST Sales Register Format
 *
 * Generates Excel report matching CA-provided template:
 * - Company header with address
 * - Sales Register with individual transaction details
 * - GSTR-1 format with GST breakdown
 * - Grand totals
 */

import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

export const taxSubmissionReportGenerator = {
  /**
   * Generate Tax Submission Report in Sales + Purchase Register format
   * @param {Object} reportData    - Data from getTaxReport API
   * @param {Object} companyInfo   - Company details for report header
   * @param {Array}  salesData     - Individual sale records from salesAPI
   * @param {Array}  purchasesData - Individual purchase records from purchaseAPI
   * @returns {Object} XLSX workbook
   */
  generateTaxSubmissionReport: (reportData, companyInfo = {}, salesData = [], purchasesData = []) => {
    if (!reportData) {
      throw new Error('Report data is required');
    }

    const workbook = XLSX.utils.book_new();

    const salesSheet = taxSubmissionReportGenerator.createSalesRegisterSheet(
      reportData,
      companyInfo,
      salesData
    );
    XLSX.utils.book_append_sheet(workbook, salesSheet, 'Sales Register');

    const purchaseSheet = taxSubmissionReportGenerator.createPurchaseRegisterSheet(
      reportData,
      companyInfo,
      purchasesData
    );
    XLSX.utils.book_append_sheet(workbook, purchaseSheet, 'Purchase Register');

    const fileName = `Tax_Register_${dayjs().format('DD-MMM-YYYY')}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return workbook;
  },

  /**
   * Create Sales Register Sheet matching CA template
   */
  createSalesRegisterSheet: (reportData, companyInfo, salesData = []) => {
    const rows = [];

    // ── Company header ────────────────────────────────────────────────────────
    const companyName = companyInfo.company_name || '';
    const rawAddress = companyInfo.company_address || '';
    const companyEmail = companyInfo.company_email || '';
    const companyPhone = companyInfo.company_phone || '';

    // Split address into up to 4 display lines
    let addressLines = rawAddress.split(/\n/).map(s => s.trim()).filter(Boolean);
    if (addressLines.length <= 1 && rawAddress.includes(',')) {
      addressLines = rawAddress.split(',').map(s => s.trim()).filter(Boolean);
    }

    rows.push([companyName]);
    rows.push([addressLines[0] || '']);
    rows.push([addressLines[1] || '']);
    rows.push([addressLines[2] || '']);
    rows.push([addressLines[3] || '']);

    const contactParts = [];
    if (companyEmail) contactParts.push(`E-Mail : ${companyEmail}`);
    if (companyPhone) contactParts.push(`Ph : ${companyPhone}`);
    rows.push([contactParts.join(', ')]);

    rows.push(['Sales Register']);

    const fromDate = reportData?.period?.from_date || '';
    const toDate = reportData?.period?.to_date || '';
    rows.push([`${fromDate} to ${toDate}`]);

    rows.push([]); // empty row before headers

    // ── Column headers (row index 9 → Excel row 10) ───────────────────────────
    const headers = [
      'Date',
      'Particulars',
      '',
      'Voucher Type',
      'Voucher No.',
      'GSTIN/UIN',
      'Sales Tax No.',
      'Gross Total',
      'Sales@18%',
      'CGST',
      'SGST',
      'ROUND OFF'
    ];
    rows.push(headers);

    // ── Individual sales rows ─────────────────────────────────────────────────
    let totalGross = 0;
    let totalSales = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalRoundOff = 0;

    const sorted = [...salesData].sort((a, b) => {
      const da = dayjs(a.sale_date || a.created_at || 0);
      const db = dayjs(b.sale_date || b.created_at || 0);
      return da.isBefore(db) ? -1 : da.isAfter(db) ? 1 : 0;
    });

    sorted.forEach(sale => {
      const saleDate = dayjs(sale.sale_date || sale.created_at).format('DD-MMM-YYYY');
      const custName = sale.customer_name || '';
      const invoiceNo = sale.invoice_no || '';
      const custGST = sale.customer_gst || '';
      const grossTotal = parseFloat(sale.grand_total || 0);
      const taxable = parseFloat(sale.subtotal || 0);
      const roundOff = parseFloat(sale.round_off || 0);

      // Use header-level cgst/sgst if stored; else split total tax equally (intra-state)
      const totalTax = parseFloat(sale.tax || 0);
      const cgst = parseFloat(sale.cgst || sale.cgst_amount || 0) || (totalTax / 2);
      const sgst = parseFloat(sale.sgst || sale.sgst_amount || 0) || (totalTax / 2);

      rows.push([
        saleDate,
        custName,
        '',
        'Sales',
        invoiceNo,
        custGST,
        '',
        grossTotal.toFixed(3),
        taxable.toFixed(3),
        cgst.toFixed(3),
        sgst.toFixed(3),
        roundOff.toFixed(3)
      ]);

      totalGross += grossTotal;
      totalSales += taxable;
      totalCGST += cgst;
      totalSGST += sgst;
      totalRoundOff += roundOff;
    });

    // ── Grand Total row ───────────────────────────────────────────────────────
    rows.push([]);
    rows.push([
      '',
      'Grand Total',
      '',
      '',
      '',
      '',
      '',
      totalGross.toFixed(3),
      totalSales.toFixed(3),
      totalCGST.toFixed(3),
      totalSGST.toFixed(3),
      totalRoundOff.toFixed(3)
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet(rows);

    // ── Column widths ─────────────────────────────────────────────────────────
    worksheet['!cols'] = [
      { wch: 14 },  // Date
      { wch: 30 },  // Particulars
      { wch: 5 },  // (empty)
      { wch: 13 },  // Voucher Type
      { wch: 15 },  // Voucher No.
      { wch: 20 },  // GSTIN/UIN
      { wch: 14 },  // Sales Tax No.
      { wch: 14 },  // Gross Total
      { wch: 14 },  // Sales@18%
      { wch: 12 },  // CGST
      { wch: 12 },  // SGST
      { wch: 12 }   // ROUND OFF
    ];

    // ── Company name bold ─────────────────────────────────────────────────────
    if (worksheet['A1']) {
      worksheet['A1'].s = { font: { bold: true, sz: 12 } };
    }

    // "Sales Register" label bold (row index 6)
    const srRef = XLSX.utils.encode_cell({ r: 6, c: 0 });
    if (worksheet[srRef]) {
      if (!worksheet[srRef].s) worksheet[srRef].s = {};
      worksheet[srRef].s.font = { bold: true, sz: 11 };
    }

    // ── Borders: header row + all data rows + Grand Total row ─────────────────
    const thinBorder = {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } }
    };

    const numCols = headers.length;
    const headerRowIdx = 9;            // 0-based index of column-header row
    const totalRowsLen = rows.length;

    for (let rowIdx = headerRowIdx; rowIdx < totalRowsLen; rowIdx++) {
      for (let colIdx = 0; colIdx < numCols; colIdx++) {
        const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
        if (!worksheet[cellRef]) continue;
        if (!worksheet[cellRef].s) worksheet[cellRef].s = {};
        worksheet[cellRef].s.border = thinBorder;

        if (rowIdx === headerRowIdx) {
          worksheet[cellRef].s.font = { bold: true };
          worksheet[cellRef].s.fill = { patternType: 'solid', fgColor: { rgb: 'D3D3D3' } };
        }
      }
    }

    // ── Particulars (customer name) bold in data rows ─────────────────────────
    const dataStartIdx = headerRowIdx + 1;
    const grandTotalRowIdx = totalRowsLen - 1;

    for (let rowIdx = dataStartIdx; rowIdx < grandTotalRowIdx - 1; rowIdx++) {
      const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: 1 });
      if (!worksheet[cellRef]) continue;
      if (!worksheet[cellRef].s) worksheet[cellRef].s = {};
      worksheet[cellRef].s.font = { bold: true };
    }

    // ── Grand Total label: right-aligned + bold ───────────────────────────────
    const gtLabelRef = XLSX.utils.encode_cell({ r: grandTotalRowIdx, c: 1 });
    if (worksheet[gtLabelRef]) {
      if (!worksheet[gtLabelRef].s) worksheet[gtLabelRef].s = {};
      worksheet[gtLabelRef].s.alignment = { horizontal: 'right' };
      worksheet[gtLabelRef].s.font = { bold: true };
    }

    // ── Grand Total numeric columns bold ─────────────────────────────────────
    for (let colIdx = 7; colIdx < numCols; colIdx++) {
      const cellRef = XLSX.utils.encode_cell({ r: grandTotalRowIdx, c: colIdx });
      if (!worksheet[cellRef]) continue;
      if (!worksheet[cellRef].s) worksheet[cellRef].s = {};
      worksheet[cellRef].s.font = { bold: true };
    }

    return worksheet;
  },

  /**
   * Create Purchase Register Sheet matching CA template format
   */
  createPurchaseRegisterSheet: (reportData, companyInfo, purchasesData = []) => {
    const rows = [];

    // ── Company header ────────────────────────────────────────────────────────
    const companyName = companyInfo.company_name || '';
    const rawAddress = companyInfo.company_address || '';
    const companyEmail = companyInfo.company_email || '';
    const companyPhone = companyInfo.company_phone || '';

    let addressLines = rawAddress.split(/\n/).map(s => s.trim()).filter(Boolean);
    if (addressLines.length <= 1 && rawAddress.includes(',')) {
      addressLines = rawAddress.split(',').map(s => s.trim()).filter(Boolean);
    }

    rows.push([companyName]);
    rows.push([addressLines[0] || '']);
    rows.push([addressLines[1] || '']);
    rows.push([addressLines[2] || '']);
    rows.push([addressLines[3] || '']);

    const contactParts = [];
    if (companyEmail) contactParts.push(`E-Mail : ${companyEmail}`);
    if (companyPhone) contactParts.push(`Ph : ${companyPhone}`);
    rows.push([contactParts.join(', ')]);

    rows.push(['Purchase Register']);

    const fromDate = reportData?.period?.from_date || '';
    const toDate = reportData?.period?.to_date || '';
    rows.push([`${fromDate} to ${toDate}`]);

    rows.push([]); // empty row before headers

    // ── Column headers ────────────────────────────────────────────────────────
    const headers = [
      'Date',
      'Particulars',
      '',
      'Voucher Type',
      'Voucher No.',
      'GSTIN/UIN',
      'Purchase Tax No.',
      'Gross Total',
      'Purchase@18%',
      'CGST',
      'SGST',
      'ROUND OFF'
    ];
    rows.push(headers);

    // ── Derive effective tax rate from summary for back-calculation ───────────
    const ps = reportData?.summary?.purchases;
    const summaryTaxable = parseFloat(ps?.total_taxable || 0);
    const summaryTax = parseFloat(ps?.total_tax || 0);
    const effectiveRate = summaryTaxable > 0
      ? (summaryTax / summaryTaxable)
      : (parseFloat(ps?.effective_rate || 18) / 100);

    // ── Individual purchase rows ──────────────────────────────────────────────
    let totalGross = 0;
    let totalTaxable = 0;
    let totalCGST = 0;
    let totalSGST = 0;
    let totalRoundOff = 0;

    const sorted = [...purchasesData].sort((a, b) => {
      const da = dayjs(a.purchase_date || a.created_at || 0);
      const db = dayjs(b.purchase_date || b.created_at || 0);
      return da.isBefore(db) ? -1 : da.isAfter(db) ? 1 : 0;
    });

    sorted.forEach(purchase => {
      const purchDate = dayjs(purchase.purchase_date || purchase.created_at).format('DD-MMM-YYYY');
      const suppName = purchase.supplier_name || '';
      const refNo = purchase.reference_no || '';
      const suppGST = purchase.supplier_gst || purchase.gst_number || '';
      const grossTotal = parseFloat(purchase.grand_total || 0);
      const roundOff = parseFloat(purchase.round_off || 0);

      // Use stored subtotal/tax if available; else back-calculate from grand_total
      let taxable = parseFloat(purchase.subtotal || 0);
      let totalTax = parseFloat(purchase.tax || 0);
      if (taxable === 0 && grossTotal > 0) {
        taxable = grossTotal / (1 + effectiveRate);
        totalTax = grossTotal - taxable;
      }
      const cgst = parseFloat(purchase.cgst || purchase.cgst_amount || 0) || (totalTax / 2);
      const sgst = parseFloat(purchase.sgst || purchase.sgst_amount || 0) || (totalTax / 2);

      rows.push([
        purchDate,
        suppName,
        '',
        'Purchase',
        refNo,
        suppGST,
        '',
        grossTotal.toFixed(3),
        taxable.toFixed(3),
        cgst.toFixed(3),
        sgst.toFixed(3),
        roundOff.toFixed(3)
      ]);

      totalGross += grossTotal;
      totalTaxable += taxable;
      totalCGST += cgst;
      totalSGST += sgst;
      totalRoundOff += roundOff;
    });

    // ── Grand Total row ───────────────────────────────────────────────────────
    rows.push([]);
    rows.push([
      '',
      'Grand Total',
      '',
      '',
      '',
      '',
      '',
      totalGross.toFixed(3),
      totalTaxable.toFixed(3),
      totalCGST.toFixed(3),
      totalSGST.toFixed(3),
      totalRoundOff.toFixed(3)
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet(rows);

    // ── Column widths ─────────────────────────────────────────────────────────
    worksheet['!cols'] = [
      { wch: 14 },  // Date
      { wch: 30 },  // Particulars
      { wch: 5 },  // (empty)
      { wch: 13 },  // Voucher Type
      { wch: 15 },  // Voucher No.
      { wch: 20 },  // GSTIN/UIN
      { wch: 14 },  // Purchase Tax No.
      { wch: 14 },  // Gross Total
      { wch: 14 },  // Purchase@18%
      { wch: 12 },  // CGST
      { wch: 12 },  // SGST
      { wch: 12 }   // ROUND OFF
    ];

    // ── Company name bold ─────────────────────────────────────────────────────
    if (worksheet['A1']) {
      worksheet['A1'].s = { font: { bold: true, sz: 12 } };
    }

    // "Purchase Register" label bold (row index 6)
    const prRef = XLSX.utils.encode_cell({ r: 6, c: 0 });
    if (worksheet[prRef]) {
      if (!worksheet[prRef].s) worksheet[prRef].s = {};
      worksheet[prRef].s.font = { bold: true, sz: 11 };
    }

    // ── Borders: header row + all data rows + Grand Total row ─────────────────
    const thinBorder = {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } }
    };

    const numCols = headers.length;
    const headerRowIdx = 9;
    const totalRowsLen = rows.length;

    for (let rowIdx = headerRowIdx; rowIdx < totalRowsLen; rowIdx++) {
      for (let colIdx = 0; colIdx < numCols; colIdx++) {
        const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
        if (!worksheet[cellRef]) continue;
        if (!worksheet[cellRef].s) worksheet[cellRef].s = {};
        worksheet[cellRef].s.border = thinBorder;

        if (rowIdx === headerRowIdx) {
          worksheet[cellRef].s.font = { bold: true };
          worksheet[cellRef].s.fill = { patternType: 'solid', fgColor: { rgb: 'D3D3D3' } };
        }
      }
    }

    // ── Particulars (supplier name) bold in data rows ─────────────────────────
    const dataStartIdx = headerRowIdx + 1;
    const grandTotalRowIdx = totalRowsLen - 1;

    for (let rowIdx = dataStartIdx; rowIdx < grandTotalRowIdx - 1; rowIdx++) {
      const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: 1 });
      if (!worksheet[cellRef]) continue;
      if (!worksheet[cellRef].s) worksheet[cellRef].s = {};
      worksheet[cellRef].s.font = { bold: true };
    }

    // ── Grand Total label: right-aligned + bold ───────────────────────────────
    const gtLabelRef = XLSX.utils.encode_cell({ r: grandTotalRowIdx, c: 1 });
    if (worksheet[gtLabelRef]) {
      if (!worksheet[gtLabelRef].s) worksheet[gtLabelRef].s = {};
      worksheet[gtLabelRef].s.alignment = { horizontal: 'right' };
      worksheet[gtLabelRef].s.font = { bold: true };
    }

    // ── Grand Total numeric columns bold ─────────────────────────────────────
    for (let colIdx = 7; colIdx < numCols; colIdx++) {
      const cellRef = XLSX.utils.encode_cell({ r: grandTotalRowIdx, c: colIdx });
      if (!worksheet[cellRef]) continue;
      if (!worksheet[cellRef].s) worksheet[cellRef].s = {};
      worksheet[cellRef].s.font = { bold: true };
    }

    return worksheet;
  }
};

export default taxSubmissionReportGenerator;
